import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    AfterViewInit,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core';
import { ICommentActionData, ICommentModel } from '@fi-shared/fi-comments/fi-comments';
import { Observable, Subject, takeUntil, filter, BehaviorSubject } from 'rxjs';
import { FiSignalRService } from '@fi-shared/fi-signal-r/fi-signal-r.service';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { ActionType, FiAttachmentDisplaySize, ObjectTypes } from '@fi-shared/types';
import { ICommentsUpdatedEventArgs } from '@fi-shared/fi-signal-r/fi-signal-r';
import { FiCommentsService } from '../fi-comments.service';
import { FiCommentsListStateService } from './services/fi-comments-list-state.service';
import { FiCommentsListHelperService } from './services/fi-comments-list-helper.service';
import { LoadingService } from '@fi-app/shared/fi-loading/loading.service';
import { isDefined } from '@fi-app/shared/utils/is-defined';
import { Animations } from '@fi-app/shared/utils/animations';
import { guid } from '@fi-shared/utils/guid';
import { fiItemsFlatten } from '@fi-app/shared/utils/fi-items-flatten';
import { isNullOrUndefined } from '@fi-app/shared/utils/is-null-or-undefined';

@Component({
    selector: 'fi-comments-list',
    templateUrl: './fi-comments-list.component.html',
    styleUrls: ['./fi-comments-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-comments-list' },
    standalone: false,
    animations: [Animations.fadeHeight()],
})
export class FiCommentsListComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
    @Input({ required: true }) objectId: number;
    @Input({ required: true }) objectType: ObjectTypes;
    @Input({ required: true }) hasDeleteAccessOfOwnerItem: boolean;
    @Input({ required: true }) initialComments: ICommentModel[];
    @Input() commentsToShowLimit: number;
    @Input() isDisabled: boolean = false;
    @Input() isAddCommentSectionShown: boolean = true;
    @Input() isCommentFooterHidden: boolean = false;
    @Input() isSimplifiedLayout: boolean = false;
    @Input() attachmentsDisplaySize: FiAttachmentDisplaySize = FiAttachmentDisplaySize.Medium;
    @Input() scrollToCommentId: number;

    protected expandCommentReplies$ = new BehaviorSubject<number | undefined>(undefined);

    private isInitialized = false;
    public readonly instanceId: string = guid();

    constructor(
        private userDataService: FiUserDataService,
        private fiSignalRService: FiSignalRService,
        private commentsService: FiCommentsService,
        private commentsListStateService: FiCommentsListStateService,
        private commentsListHelperService: FiCommentsListHelperService,
        private loadingService: LoadingService,
        private cdr: ChangeDetectorRef,
    ) {}

    protected get visibleComments$(): Observable<ICommentModel[]> {
        return this.commentsListStateService.getVisibleComments$(this.objectType, this.objectId);
    }

    public ngOnInit(): void {
        this.subscribeOnSignalRCommentsUpdated();
        this.subscribeOnDeleteComment();
        this.subscribeOnSaveComment();
    }

    public ngAfterViewInit(): void {
        // wait 300ms to ensure the comments list is visible
        setTimeout(() => {
            this.scrollToComment();
        }, 300);
    }

    private scrollToComment(): void {
        if (isNullOrUndefined(this.scrollToCommentId)) {
            return;
        }

        const allComments = fiItemsFlatten(this.commentsListStateService.getComments(this.objectType, this.objectId));
        const currentComment = allComments.find((comment: ICommentModel) => comment.Id === +this.scrollToCommentId);
        if (isNullOrUndefined(currentComment)) {
            return;
        }

        const commentElementId = this.commentsListHelperService.getCommentId(this.scrollToCommentId);
        const commentElement = document.getElementById(commentElementId);

        const scrollToComment = (element: HTMLElement) => {
            if (!isNullOrUndefined(element)) {
                element.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'nearest',
                });
            }
        };

        if (!isNullOrUndefined(commentElement)) {
            scrollToComment(commentElement);
            return;
        }

        if (!isNullOrUndefined(currentComment.ParentId)) {
            const commentIdToExpand = this.getRootCommentId(currentComment, allComments);
            this.expandCommentReplies$.next(commentIdToExpand);
            setTimeout(() => {
                const commentElement = document.getElementById(commentElementId);
                if (!isNullOrUndefined(commentElement)) {
                    scrollToComment(commentElement);
                    return;
                }
            }, 300); // 300 is expanding animation duration
            return;
        }
    }

    private getRootCommentId(comment: ICommentModel, allComments: ICommentModel[]): number {
        let rootComment = comment;
        let parentComment = allComments.find((item) => item.Id === rootComment.ParentId);

        while (!isNullOrUndefined(parentComment)) {
            rootComment = parentComment;
            parentComment = allComments.find((item) => item.Id === rootComment.ParentId);
        }

        return rootComment.Id;
    }

    public getCommentsCount(): number {
        return this.commentsListStateService.getCommentsCount(this.objectType, this.objectId);
    }

    public ngOnChanges(changes: SimpleChanges): void {
        const requiredInputs = ['objectId', 'objectType', 'initialComments'];
        const hasAllRequiredInputs = requiredInputs.every((input) => this[input] !== undefined && this[input] !== null);

        if (!hasAllRequiredInputs) {
            return;
        }

        if (!this.isInitialized) {
            this.initializeComments();
        } else if (changes['initialComments']?.currentValue) {
            // Re-initialize if comments change after initial setup
            this.initializeComments();
        }
    }

    private initializeComments(): void {
        this.commentsListStateService.setComments(
            this.initialComments,
            this.commentsToShowLimit,
            this.objectType,
            this.objectId,
        );
        this.isInitialized = true;
        this.cdr.detectChanges();
    }

    protected isViewAllCommentsVisible(): boolean {
        if (!isDefined(this.commentsToShowLimit)) {
            return false;
        }
        const comments = this.commentsListStateService.getComments(this.objectType, this.objectId);
        const visibleComments = this.commentsListStateService.getVisibleComments(this.objectType, this.objectId);
        return comments.length > this.commentsToShowLimit && visibleComments.length < comments.length;
    }

    public showAllComments(): void {
        this.commentsListStateService.showAllComments(this.objectType, this.objectId);
    }

    protected getCommentId(commentId: number): string {
        return this.commentsListHelperService.getCommentId(commentId);
    }

    private subscribeOnDeleteComment(): void {
        this.commentsListStateService.deleteComment$
            .pipe(
                takeUntil(this.ngOnDestroy$),
                filter((commentActionData) => this.isCommentActionForCurrentObject(commentActionData)),
            )
            .subscribe((commentActionData) => {
                this.commentsListHelperService
                    .showDeleteConfirmationDialog(commentActionData.Comment.ChildItems?.length > 0)
                    .onYesButtonClick(() => this.deleteComment(commentActionData.Comment));
            });
    }

    private subscribeOnSaveComment(): void {
        this.commentsListStateService.saveComment$
            .pipe(
                takeUntil(this.ngOnDestroy$),
                filter((commentActionData) => this.isCommentActionForCurrentObject(commentActionData)),
            )
            .subscribe((commentActionData) => {
                /*
                const commentToSave: ICommentModel = cloneDeep(commentActionData.Comment);
                commentToSave.Content = this.textEditorTagUsersService.getConvertedFromEditToViewModeUserTags(
                    commentToSave.Content,
                );
*/
                this.loadingService
                    .trackPromise(this.commentsService.saveComment(commentActionData.Comment))
                    .then((commentId) => {
                        commentActionData.Comment.Id = commentId;
                        this.commentsListStateService.handleUpsertComment(
                            commentActionData.Comment,
                            this.objectType,
                            this.objectId,
                        );
                    });
            });
    }

    private isCommentActionForCurrentObject(commentActionData: ICommentActionData): boolean {
        return (
            commentActionData.ObjectId === this.objectId &&
            commentActionData.ObjectType === this.objectType &&
            commentActionData.InstanceId === this.instanceId
        );
    }

    private deleteComment(comment: ICommentModel): void {
        this.loadingService.trackPromise(
            this.commentsService.deleteComments([comment.Id]).then(() => {
                this.commentsListStateService.handleRemoveComment(comment, this.objectType, this.objectId);
            }),
        );
    }

    private subscribeOnSignalRCommentsUpdated(): void {
        this.fiSignalRService.commentsUpdatedEvent.subscribe((data) => {
            if (!this.isCommentUpdatedByAnotherUser(data)) {
                return;
            }
            // data.comments -> comments to add/update/remove
            data.Comments.forEach((comment) => {
                this.handleCommentAction(data.ActionType, comment);
            });
        }, this.ngOnDestroy$);
    }

    private handleCommentAction(action: ActionType, comment: ICommentModel): void {
        if (action === ActionType.Create || action === ActionType.Update) {
            this.commentsListStateService.handleUpsertComment(comment, this.objectType, this.objectId);
        } else if (action === ActionType.Delete) {
            this.commentsListStateService.handleRemoveComment(comment, this.objectType, this.objectId);
        }
    }

    private isCommentUpdatedByAnotherUser(data: ICommentsUpdatedEventArgs): boolean {
        return (
            this.objectId === data.ObjectId &&
            this.objectType === data.ObjectType &&
            this.userDataService.getUserId() !== data.CurrentUserId
        );
    }

    private ngOnDestroy$ = new Subject<void>();
    public ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
